import { Award } from 'lucide-react'
import { useLocation } from '@tanstack/react-router'
import { useStatePack } from '@/lib/state-context'
import { useDemoOverlay } from './demo-overlay-state'

/**
 * Slim partnership strip rendered below the site header when the active
 * pack has a tenant overlay AND the visitor is on a state page. Returns
 * null otherwise, so the banner never shows on the homepage, course
 * pages, or anywhere outside /states/<slug>.
 *
 * Branding intensity comes from the demo overlay state:
 *   subtle    → hidden entirely
 *   cobranded → the standard slim violet strip
 *   sponsored → larger pill with the sponsor's primary color and tagline
 */
export default function TenantPartnershipBanner() {
  const pack = useStatePack()
  const location = useLocation()
  const overlay = useDemoOverlay()
  const tenant = pack.tenant
  const onStatePage = /^\/states\/[a-z][a-z0-9-]+/i.test(location.pathname)
  if (!tenant || !onStatePage) return null
  if (overlay.branding === 'subtle') return null

  const label =
    tenant.sponsorStrip?.label ?? `In partnership with ${tenant.brandName}`
  const accent = tenant.primaryColor ?? '#7835f8'
  const isSponsored = overlay.branding === 'sponsored'

  return (
    <div
      className="border-b border-white/10 text-white"
      style={{ background: isSponsored ? accent : 'rgb(120 53 248)' }}
    >
      <div
        className={`mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 ${
          isSponsored ? 'py-3 text-sm sm:text-base' : 'py-2 text-sm'
        }`}
      >
        <p className="inline-flex items-center gap-2.5 font-medium">
          <span
            className={`flex shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25 ${
              isSponsored ? 'h-8 w-8' : 'h-6 w-6'
            }`}
          >
            <Award
              className={`text-white ${isSponsored ? 'h-4 w-4' : 'h-3.5 w-3.5'}`}
            />
          </span>
          <span className={isSponsored ? 'font-semibold' : ''}>{label}</span>
        </p>
        {tenant.tagline && (
          <p className="text-xs text-white/85 sm:text-sm">{tenant.tagline}</p>
        )}
      </div>
    </div>
  )
}
